import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { hasRoleAccess } from "../../utils/rolePermissions";
import {
  DashboardIcon,
  ProductsIcon,
  IntegrationsIcon,
  SettingsIcon,
  InviteIcon,
  OrdersIcon,
} from "./SidebarIcons";
import "./Sidebar.css";

const Sidebar = ({ isOpen, onToggle }) => {
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { user } = useAuth();

  const userRole = user?.role || "";

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSubmenu = (menuKey) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey],
    }));
  };

  const allMenuItems = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <DashboardIcon />,
      path: "/dashboard",
      roles: ["owner", "admin"],
    },
    {
      key: "smartscan",
      label: "Smart Scan",
      icon: <ProductsIcon />,
      path: "/sorter",
      roles: ["owner", "admin", "editor", "seller", "seller manager"],
    },
    {
      key: "products",
      label: "Products",
      icon: <ProductsIcon />,
      roles: ["owner", "admin", "editor", "seller", "seller manager"],
      submenu: [
        {
          label: "Add Product",
          path: "/products/add",
          roles: ["owner", "admin", "editor", "seller", "seller manager"],
        },
        {
          label: "View Products",
          path: "/products",
          roles: ["owner", "admin", "editor", "seller", "seller manager"],
        },
        {
          label: "View Batches",
          path: "/batches",
          roles: ["owner", "admin", "seller manager"],
        },
      {
        label: "Vinted Products",
          path: "/vinted-products",
          roles: ["owner", "admin", "editor", "seller manager"],
        },
      ],
    },
    {
      key: "orders",
      label: "Orders",
      icon: <OrdersIcon />,
      roles: [
        "owner",
        "admin",
        "seller",
        "seller manager",
        "rider",
        "outbound",
      ],
      submenu: [
        {
          label: "Orders",
          path: "/orders",
          roles: ["owner", "admin", "seller", "rider", "outbound","seller manager"],
        },
        {
          label: "Order Dashboard",
          path: "/order-dashboard",
          roles: ["owner", "admin", "seller manager", "seller"],
        },
        {
          label: "Vendor Payouts",
          path: "/vendor-payouts",
          roles: ["owner", "admin"],
        },
        {
          label: "PnL",
          path: "/pnl",
          roles: ["owner", "admin"],
        },
      ],
    },
    {
      key: "integrations",
      label: "Marketplace Integration",
      icon: <IntegrationsIcon />,
      roles: ["owner", "admin"],
      submenu: [
        {
          label: "Shopify",
          path: "/integrations/shopify",
          roles: ["owner", "admin"],
        },
        {
          label: "eBay",
          path: "/integrations/ebay",
          roles: ["owner", "admin"],
        },
        {
          label: "Poshmark",
          path: "/integrations/poshmark",
          roles: ["owner", "admin"],
        },
      ],
    },
    {
      key: "settings",
      label: "Settings",
      icon: <SettingsIcon />,
      roles: ["owner", "admin", "seller manager"],
      submenu: [
        {
          label: "General",
          path: "/settings/general",
          roles: ["owner", "admin", "seller manager"],
        },
        {
          label: "eBay Settings",
          path: "/settings/ebay",
          roles: ["owner", "admin"],
        },
        {
          label: "Shopify Settings",
          path: "/settings/shopify",
          roles: ["owner", "admin"],
        },
      ],
    },
    {
      key: "invite",
      label: "Users",
      icon: <InviteIcon />,
      path: "/invite-users",
      roles: ["owner", "admin", "seller manager"],
    },
  ];

  const menuItems = allMenuItems
    .map((item) => {
      if (item.submenu) {
        const filteredSubmenu = item.submenu.filter((subItem) =>
          hasRoleAccess(userRole, subItem.roles || item.roles),
        );
        if (filteredSubmenu.length === 0) return null;
        if (!hasRoleAccess(userRole, item.roles)) return null;
        return { ...item, submenu: filteredSubmenu };
      }
      return hasRoleAccess(userRole, item.roles) ? item : null;
    })
    .filter(Boolean);

  return (
    <>
      {isOpen && isMobile && (
        <div className="sidebar-overlay" onClick={onToggle}></div>
      )}

      {isMobile && !isOpen && (
        <button
          className="mobile-sidebar-toggle"
          onClick={onToggle}
          aria-label="Open sidebar"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
      )}

      <aside className={`sidebar ${isOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-header">
          {isMobile && isOpen ? (
            <div className="sidebar-mobile-header">
              <button
                className="sidebar-close-btn"
                onClick={onToggle}
                aria-label="Close sidebar"
              >
                ×
              </button>
            </div>
          ) : (
            <>
              {!isOpen ? (
                <button
                  className="sidebar-hamburger-btn"
                  onClick={onToggle}
                  aria-label="Open sidebar"
                >
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                </button>
              ) : (
                <button
                  className="sidebar-close-btn desktop"
                  onClick={onToggle}
                  aria-label="Close sidebar"
                >
                  ×
                </button>
              )}
            </>
          )}
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <div key={item.key}>
              {item.submenu ? (
                <>
                  <button
                    className={`sidebar-item sidebar-toggle ${expandedMenus[item.key] ? "expanded" : ""}`}
                    onClick={() => toggleSubmenu(item.key)}
                  >
                    <span className="sidebar-icon">{item.icon}</span>
                    {isOpen && (
                      <>
                        <span className="sidebar-label">{item.label}</span>
                        <span className="sidebar-arrow">
                          {expandedMenus[item.key] ? "▼" : "▶"}
                        </span>
                      </>
                    )}
                  </button>

                  {expandedMenus[item.key] && isOpen && (
                    <div className="sidebar-submenu">
                      {item.submenu.map((subItem) => (
                        <NavLink
                          key={subItem.path}
                          to={subItem.path}
                          className={({ isActive }) =>
                            `sidebar-subitem ${isActive ? "active" : ""}`
                          }
                        >
                          {subItem.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `sidebar-item ${isActive ? "active" : ""}`
                  }
                >
                  <span className="sidebar-icon">{item.icon}</span>
                  {isOpen && (
                    <span className="sidebar-label">{item.label}</span>
                  )}
                </NavLink>
              )}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
